import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Box, Button, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider, Alert, Menu, CircularProgress,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  HomeWork as RowHouseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import {
  downloadExcelFile,
  exportAsCSV,
  exportAsExcel,
  exportAsPDF,
  readExcelFile,
} from '../../utils/importExport';
import { getProjects, getPlotsByProject } from '../../services/project';
import { plotService } from '../../services/plot';
import { getFacings, getRowHouses } from '../../services/master';
import { globalMasterService } from '../../services/stateAPI';
import { hasReadPermission, hasWritePermission } from '../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';

const initialRowHouses = [
  // {
  //   id: 'RH-001',
  //   project: 'Emerald Heights',
  //   number: 'Row-1',
  //   width: '25 ft',
  //   depth: '40 ft',
  //   size: '1000 sq.ft',
  //   type: '3 BHK',
  //   facing: 'East',
  //   rate: '₹9,500 / sq.ft',
  //   plcPercent: '4%',
  //   amount: '₹95,00,000',
  //   guidelineValue: '₹80,00,000',
  //   status: '',
  //   remark: 'Ready for possession by Sep 2025',
  // },
  // {
  //   id: 'RH-002',
  //   project: 'Emerald Heights',
  //   number: 'Row-2',
  //   width: '28 ft',
  //   depth: '38 ft',
  //   size: '1064 sq.ft',
  //   type: '4 BHK',
  //   facing: 'North',
  //   rate: '₹10,200 / sq.ft',
  //   plcPercent: '5%',
  //   amount: '₹1,08,00,000',
  //   guidelineValue: '₹92,00,000',
  //   status: 'Booked',
  //   remark: 'Loan processing in progress',
  // },
  // {
  //   id: 'RH-003',
  //   project: 'Lakeview Residences',
  //   number: 'Row-7',
  //   width: '24 ft',
  //   depth: '35 ft',
  //   size: '840 sq.ft',
  //   type: '2 BHK',
  //   facing: 'West',
  //   rate: '₹8,200 / sq.ft',
  //   plcPercent: '3%',
  //   amount: '₹68,88,000',
  //   guidelineValue: '₹60,00,000',
  //   status: 'Hold',
  //   remark: 'Awaiting documentation',
  // },
];

const statusChipStyle = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'available':
      return { backgroundColor: 'rgba(46, 125, 50, 0.12)', color: '#2e7d32' };
    case 'booked':
      return { backgroundColor: 'rgba(25, 118, 210, 0.12)', color: '#1976d2' };
    case 'underConstruction':
      return { backgroundColor: 'rgba(255, 193, 7, 0.12)', color: '#f57c00' };
    case 'hold':
      return { backgroundColor: 'rgba(244, 67, 54, 0.12)', color: '#d32f2f' };
    default:
      return { backgroundColor: 'rgba(158, 158, 158, 0.12)', color: '#616161' };
  }
};

const RowHouse = () => {
  const location = useLocation();
  const history = useHistory();
  const initialProjectName = location?.state?.projectName || '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canRead = hasReadPermission('project.row-house') || hasReadPermission('project');
  const canWrite = hasWritePermission('project.row-house') || hasWritePermission('project');

  const [rowHouses, setRowHouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [facings, setFacings] = useState([]);
  const [rowHouseTypes, setRowHouseTypes] = useState([]);
  const [progressOptions, setProgressOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [loadingRowHouses, setLoadingRowHouses] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [projectFilter, setProjectFilter] = useState('');
  const [pendingProjectFilter, setPendingProjectFilter] = useState('');
  const [selectedRowHouse, setSelectedRowHouse] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editProjectInput, setEditProjectInput] = useState('');
  const [editTypeInput, setEditTypeInput] = useState('');
  const [editFacingInput, setEditFacingInput] = useState('');
  const fileInputRef = useRef(null);
  const [amenityOptions, setAmenityOptions] = useState([]);
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);
  const [totalRowHouses, setTotalRowHouses] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const projectsData = await getProjects({ projectType: 'RowHouse' });
        setProjects(projectsData || []);
        if (!projectsData || !projectsData.length) {
          setErrorMessage('No Projects found');
        }
      } catch (error) {
        setProjects([]);
        setErrorMessage('No Projects found');
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchFacingData = async () => {
      try {
        const facingsData = await getFacings();
        setFacings(facingsData || []);
      } catch (error) {
        setFacings([]);
      }
    };

    const fetchRowHouseTypes = async () => {
      try {
        const rowHouseTypesData = await getRowHouses();
        setRowHouseTypes(rowHouseTypesData || []);
      } catch (error) {
        setRowHouseTypes([]);
      }
    };

    fetchFacingData();
    fetchRowHouseTypes();
    fetchAmenities();
  }, []);

  const fetchAmenities = async () => {
    try {
      const amenities = await globalMasterService.getAmenities();
      setAmenityOptions(amenities || []);
    } catch (error) {
      console.error('Failed to load amenities', error);
    }
  };

  useEffect(() => {
    const fetchProgressOptions = async () => {
      setLoadingProgress(true);
      try {
        const progressData = await globalMasterService.getProgress();
        // Filter to only include the 4 required status options
        const filteredProgressData = (progressData || []).filter(option => {
          const statusValue = (option.value || option.id || '').toLowerCase();
          const statusLabel = (option.label || '').toLowerCase();

          // Check for our required statuses (case-insensitive, partial matches)
          return statusValue.includes('available') || statusLabel.includes('available') ||
            (statusValue.includes('under') && statusValue.includes('construct')) ||
            (statusLabel.includes('under') && statusLabel.includes('construct')) ||
            statusValue.includes('book') || statusLabel.includes('book') ||
            statusValue.includes('hold') || statusLabel.includes('hold');
        });

        // Normalize the data structure to ensure consistent property names
        const normalizedProgressData = filteredProgressData.map(option => ({
          value: option.value || option.id,
          label: option.label
        }));

        // If we don't have all 4 required options from API, use fallback
        const finalProgressData = normalizedProgressData.length >= 4 ? normalizedProgressData : [
          { value: 'Available', label: 'Available' },
          { value: 'Booked', label: 'Booked' },
          { value: 'underConstruction', label: 'Under Construction' },
          { value: 'Hold', label: 'Hold' },
        ];

        // Log for debugging - remove after confirming filtering works
        console.log('Raw progress options from API:', progressData);
        console.log('Filtered progress options:', filteredProgressData);
        console.log('Normalized progress options:', normalizedProgressData);
        console.log('Final progress options:', finalProgressData);

        setProgressOptions(finalProgressData);
      } catch (error) {
        console.error('Failed to fetch progress options', error);
        // Fallback options if API fails
        const fallbackOptions = [
          { value: 'Available', label: 'Available' },
          { value: 'Booked', label: 'Booked' },
          { value: 'underConstruction', label: 'Under Construction' },
          { value: 'Hold', label: 'Hold' },
        ];
        console.log('API failed, using fallback progress options:', fallbackOptions);
        setProgressOptions(fallbackOptions);
      } finally {
        setLoadingProgress(false);
      }
    };

    fetchProgressOptions();
  }, []);

  const projectOptions = useMemo(() => {
    return Array.isArray(projects) ? projects : [];
  }, [projects]);

  const fetchRowHouseInventory = useCallback(
    async (pageNumber = 0, projectId = pendingProjectFilter) => {
      if (projects.length === 0 || loadingProjects) return;
      if (!projectId) {
        setRowHouses([]);
        setTotalRowHouses(0);
        return;
      }
      setLoadingRowHouses(true);
      try {
        const targetProjectId = projectId;
        const selectedProject = projects.find((p) => String(p._id || p.id) === String(targetProjectId));

        const response = await getPlotsByProject(
          targetProjectId,
          'RowHouse',
          'RowHouse',
          null,
          pageNumber + 1,
          PAGE_SIZE
        );

        const inventories = response?.inventories || response?.data?.inventories || [];

        if (!Array.isArray(inventories)) {
          setRowHouses([]);
          setTotalRowHouses(0);
          return;
        }

        const activeInventories = inventories.filter(item => !item.isDeleted && !item.isdeleted);

        const mappedRowHouses = activeInventories.map((item, index) => {
          const plotName = item.plotName || '';
          const normalizedPlotName = plotName.replace(/^RAW-/, 'Raw house ');
          const widthValue = item.width ?? 0;
          const lengthValue = item.length ?? 0;
          const totalSizeValue = item.totalSize ?? 0;
          const facingValue = item.facing || '';
          const pricePerSqFtValue = item.pricePerSqFt ?? 0;
          const companyDiscountPercentValue = item.companyDiscountPercent ?? 0;
          const plcPercentValue = item.plcPercent ?? 0;
          const amountValue = item.amount ?? 0;
          const guidelineValue = item.guidelineValue ?? 0;
          const statusValue = (item.status || 'select status').toLowerCase();
          const remarkValue = item.remark || '';

          const discountPerSqFtValue = item.discountPerSqFt ?? 0;
          const discountAmountValue = item.discountAmount ?? 0;

          const formattedWidth = widthValue > 0 ? `${widthValue} ft` : widthValue === 0 ? '0 ft' : '—';
          const formattedDepth = lengthValue > 0 ? `${lengthValue} ft` : lengthValue === 0 ? '0 ft' : '—';
          const formattedSize = totalSizeValue > 0 ? `${totalSizeValue} sq.ft` : totalSizeValue === 0 ? '0 sq.ft' : '—';
          const formattedRate = pricePerSqFtValue > 0
            ? `₹${pricePerSqFtValue.toLocaleString('en-IN')} / sq.ft`
            : pricePerSqFtValue === 0 ? '₹0 / sq.ft' : '—';
          const formattedDiscount = `${companyDiscountPercentValue || 0}%`;
          const formattedDiscountPerSqFt = discountPerSqFtValue > 0 ? `₹${discountPerSqFtValue.toLocaleString('en-IN')}` : '₹0';
          const formattedDiscountAmount = discountAmountValue > 0 ? `₹${discountAmountValue.toLocaleString('en-IN')}` : '₹0';
          const formattedPlc = plcPercentValue > 0
            ? `${plcPercentValue}%`
            : plcPercentValue === 0 ? '0%' : '—';
          const formattedAmount = amountValue > 0
            ? `₹${amountValue.toLocaleString('en-IN')}`
            : amountValue === 0 ? '₹0' : '—';
          const formattedGuideline = guidelineValue > 0
            ? `₹${guidelineValue.toLocaleString('en-IN')}`
            : guidelineValue === 0 ? '₹0' : '—';

          const projectName = item.projectData?.projectName || selectedProject?.projectName || 'Unknown Project';
          const isEdit = item.isEdit !== undefined ? item.isEdit : true; // Default to true if not present

          return {
            id: item._id || item.id || `RH-${index + 1}`,
            project: projectName,
            number: normalizedPlotName || `Row-${index + 1}`, // Use normalized plotName like "Raw house 4"
            width: formattedWidth,
            depth: formattedDepth,
            size: formattedSize,
            type: item.type || 'Row House', // Default to Raw House since this is RawHouse inventory
            facing: facingValue || '—',
            rate: formattedRate,
            companyDiscountPercent: companyDiscountPercentValue || 0,
            discount: formattedDiscount,
            discountPerSqFt: discountPerSqFtValue,
            discountAmount: discountAmountValue,
            formattedDiscountPerSqFt: formattedDiscountPerSqFt,
            formattedDiscountAmount: formattedDiscountAmount,
            plcPercent: formattedPlc,
            amount: formattedAmount,
            guidelineValue: formattedGuideline,
            status: statusValue.charAt(0).toUpperCase() + statusValue.slice(1),
            remark: remarkValue || '—',
            isEdit: isEdit,
            raw: item,
          };
        });

        const pagination = response?.pagination || {};
        const totalItems = pagination.totalItems ?? pagination.total ?? response?.count ?? activeInventories.length;
        const totalPages = totalItems > 0 ? Math.ceil(totalItems / PAGE_SIZE) : 0;
        setTotalRowHouses(totalItems);

        if (totalPages && pageNumber >= totalPages) {
          setRowHouses([]);
          setPage(totalPages - 1);
          return;
        }

        setRowHouses(mappedRowHouses);
        setProjectFilter(selectedProject?.projectName || selectedProject?.name || '');
      } catch (error) {
        setErrorMessage('Failed to load row houses. Please try again.');
        setRowHouses([]);
        setTotalRowHouses(0);
      } finally {
        setLoadingRowHouses(false);
      }
    },
    [PAGE_SIZE, pendingProjectFilter, projects, loadingProjects]
  );

  useEffect(() => {
    if (pendingProjectFilter) {
      fetchRowHouseInventory(page, pendingProjectFilter);
    } else {
      setRowHouses([]);
      setTotalRowHouses(0);
    }
  }, [fetchRowHouseInventory, pendingProjectFilter, page]);

  const handleProjectChange = (event) => {
    const selectedValue = event.target.value;
    setErrorMessage('');
    const valueStr = selectedValue ? String(selectedValue) : '';
    setPendingProjectFilter(valueStr);
    const selectedProject = projects.find((p) => String(p._id || p.id) === valueStr);
    setProjectFilter(selectedProject ? (selectedProject.projectName || selectedProject.name || '') : '');
    setRowHouses([]);
    setPage(0);
    setTotalRowHouses(0);
  };

  const filteredRowHouses = useMemo(() => {
    if (!projectFilter || projectFilter === 'all') {
      return rowHouses;
    }
    return rowHouses.filter(
      (item) => (item.project || '').toLowerCase() === projectFilter.toLowerCase(),
    );
  }, [rowHouses, projectFilter]);

  const typeOptions = useMemo(() => {
    const masterTypes = rowHouseTypes
      .filter((item) => item.status === 'Active')
      .map((item) => item.rowHouse)
      .filter(Boolean);

    const existing = rowHouses
      .map((item) => item.type)
      .filter((value) => value && value.trim().length > 0);

    // If master types are available, prioritize them and drop the generic fallback "Row House"
    const baseList = masterTypes.length ? masterTypes : existing;
    const cleaned = baseList.filter((val) => {
      const lower = val.toString().trim().toLowerCase();
      return masterTypes.length === 0 || (lower !== 'row house' && lower !== 'rowhouse');
    });

    return Array.from(new Set(cleaned)).sort();
  }, [rowHouseTypes, rowHouses]);
  const facingOptions = useMemo(() => {
    return Array.isArray(facings)
      ? facings
        .filter(facing => facing.status === 'Active')
        .map(facing => facing.name)
      : [];
  }, [facings]);

  const formatCurrency = (value) => value || '—';
  const parseNumber = (value) => {
    if (!value) return null;
    const match = value.toString().match(/[\d.]+/g);
    if (!match) return null;
    const parsed = parseFloat(match.join(''));
    return Number.isNaN(parsed) ? null : parsed;
  };

  const formatSize = (widthValue, depthValue) => {
    const width = parseNumber(widthValue);
    const depth = parseNumber(depthValue);
    if (width === null || depth === null) return '';
    const area = width * depth;
    if (!Number.isFinite(area) || Number.isNaN(area)) return '';
    const formatted = Number.isInteger(area) ? area.toString() : area.toFixed(2);
    return `${formatted} sq.ft`;
  };

  const generateRowHouseId = () => {
    const numericIds = rowHouses
      .map((item) => {
        const match = (item.id || '').match(/(\d+)/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((num) => num !== null);
    const nextNumber = numericIds.length ? Math.max(...numericIds) + 1 : 1;
    return `RH-${nextNumber.toString().padStart(3, '0')}`;
  };

  const computeSizeFromDimensions = (widthValue, depthValue) => {
    const width = parseNumber(widthValue);
    const depth = parseNumber(depthValue);
    if (width !== null && depth !== null && width > 0 && depth > 0) {
      const area = width * depth;
      return Number.isInteger(area) ? area.toString() : area.toFixed(2);
    }
    return '';
  };

  const handleImportClick = () => {
    setImportError('');
    setImportSuccess('');
    setImportFileName('');
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportError('');
    setImportSuccess('');
    setImportFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReset = () => {
    setPendingProjectFilter('');
    setProjectFilter('');
    setPage(0);
    setRowHouses([]);
    setTotalRowHouses(0);
  };

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const handleImportFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    try {
      setImportFileName(file.name);
      const rows = await readExcelFile(file);
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error('Excel sheet is empty or invalid.');
      }

      const normalized = rows.map((row, index) => ({
        id: row.id || row.ID || `imported-${Date.now()}-${index}`,
        project: row.project || row.Project || '',
        number: row.number || row.Number || row['Row House No'] || '',
        width: row.width || row.Width || '',
        depth: row.depth || row.Depth || '',
        size: row.size || row.Size || '',
        type: row.type || row.Type || '',
        facing: row.facing || row.Facing || '',
        rate: row.rate || row.Rate || '',
        plcPercent: row.plcPercent || row.PLCPercent || row['PLC %'] || '',
        amount: row.amount || row.Amount || '',
        guidelineValue: row.guidelineValue || row.GuidelineValue || '',
        status: row.status || row.Status || '',
        remark: row.remark || row.Remark || '',
      }));

      setRowHouses(normalized);
      setTotalRowHouses(normalized.length);
      setImportSuccess(`Imported ${normalized.length} row house(s) successfully.`);
    } catch (err) {
      setImportError(err.message || 'Failed to read the selected file.');
      setImportSuccess('');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const buildExportRows = () => {
    return (filteredRowHouses.length ? filteredRowHouses : rowHouses).map((rowHouse) => ({
      Project: rowHouse.project,
      RowHouseNumber: rowHouse.number,
      Width: rowHouse.width,
      Depth: rowHouse.depth,
      Size: rowHouse.size,
      Type: rowHouse.type,
      Facing: rowHouse.facing,
      Rate: rowHouse.rate,
      PLCPercent: rowHouse.plcPercent,
      Amount: rowHouse.amount,
      GuidelineValue: rowHouse.guidelineValue,
      Discount: rowHouse.discount,
      Status: rowHouse.status,
      Remark: rowHouse.remark,
    }));
  };

  const showTransientSuccess = (message) => {
    setErrorMessage('');
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showTransientError = (message) => {
    setSuccessMessage('');
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 4000);
  };

  const handleExportExcel = async () => {
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const params = new URLSearchParams({
        inventoryType: 'RowHouse',
        projectType: 'RowHouse',
        page: '1',
        limit: '100000',
      });
      if (pendingProjectFilter) {
        params.set('projectId', pendingProjectFilter);
      }
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
      const response = await fetch(`${base}/api/projects/details?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const body = await response.json().catch(() => ({}));
      const inventories = Array.isArray(body?.data?.inventories)
        ? body.data.inventories
        : Array.isArray(body?.inventories)
          ? body.inventories
          : Array.isArray(body?.data)
            ? body.data
            : [];

      if (!Array.isArray(inventories) || inventories.length === 0) {
        showTransientError('No row house data available to export.');
        return;
      }

      const rows = inventories.map((item, index) => {
        const widthValue = item.width ?? 0;
        const lengthValue = item.length ?? 0;
        const totalSizeValue = item.totalSize ?? 0;
        const facingValue = item.facing || '';
        const pricePerSqFtValue = item.pricePerSqFt ?? 0;
        const plcPercentValue = item.plcPercent ?? 0;
        const amountValue = item.amount ?? 0;
        const guidelineValue = item.guidelineValue ?? 0;
        const statusValue = (item.status || 'available').toLowerCase();
        const remarkValue = item.remark || '';
        const projectName = item.projectData?.projectName || item.project || '';
        const plotName = item.plotName || '';
        const normalizedPlotName = plotName.replace(/^RAW-/, 'Raw house ');

        return {
          Project: projectName || 'Unknown Project',
          RowHouseNumber: normalizedPlotName || `Row-${index + 1}`,
          Width: widthValue,
          Depth: lengthValue,
          Size: totalSizeValue,
          Type: item.type || 'Row House',
          Facing: facingValue || '—',
          Rate: pricePerSqFtValue,
          PLCPercent: plcPercentValue,
          Amount: amountValue,
          GuidelineValue: guidelineValue,
          Status: statusValue.charAt(0).toUpperCase() + statusValue.slice(1),
          Remark: remarkValue,
        };
      });

      exportAsExcel({ rows, sheetName: 'RowHouses', fileNameBase: 'rowhouses' });
      showTransientSuccess('Row houses exported to Excel successfully!');
    } catch (err) {
      showTransientError('Failed to export row houses to Excel.');
    } finally {
      handleCloseExportMenu();
    }
  };

  const handleExportCSV = () => {
    try {
      const rows = buildExportRows();
      if (!rows.length) {
        showTransientError('No row house data available to export.');
        return;
      }
      exportAsCSV({ rows, fileNameBase: 'rowhouses' });
      showTransientSuccess('Row houses exported to CSV successfully!');
    } catch (err) {
      showTransientError('Failed to export row houses to CSV.');
    } finally {
      handleCloseExportMenu();
    }
  };

  const handleExportPDF = () => {
    try {
      const rows = buildExportRows();
      if (!rows.length) {
        showTransientError('No row house data available to export.');
        return;
      }
      const headers = Object.keys(rows[0] || {});
      exportAsPDF({
        rows,
        headers,
        title: 'Row House List',
        fileNameBase: 'rowhouses',
        headStyles: { fillColor: [33, 150, 243] },
      });
      showTransientSuccess('Row houses exported to PDF successfully!');
    } catch (err) {
      showTransientError('Failed to export row houses to PDF.');
    } finally {
      handleCloseExportMenu();
    }
  };

  const handleOpenView = (rowHouse) => {
    setSelectedRowHouse(rowHouse);
    setViewDialogOpen(true);
  };

  const handleCloseView = () => {
    setSelectedRowHouse(null);
    setViewDialogOpen(false);
  };

  const handleOpenEdit = (rowHouse) => {
    setEditFormData({
      id: rowHouse.id,
      project: rowHouse.project,
      number: rowHouse.number,
      type: rowHouse.type,
      facing: rowHouse.facing,
      status: (rowHouse.raw?.status || 'available').charAt(0).toUpperCase() + (rowHouse.raw?.status || 'available').slice(1),
      remark: rowHouse.remark,
      width: rowHouse.raw?.width || '',
      depth: rowHouse.raw?.length || '',
      size: rowHouse.raw?.totalSize || '',
      rate: rowHouse.raw?.pricePerSqFt || '',
      companyDiscountPercent: rowHouse.raw?.companyDiscountPercent || '',
      plcPercent: rowHouse.raw?.plcPercent || '',
      amount: rowHouse.raw?.amount || '',
      discountPerSqFt: rowHouse.raw?.discountPerSqFt || '',
      discountAmount: rowHouse.raw?.discountAmount || '',
      guidelineValue: rowHouse.raw?.guidelineValue || '',
      raw: rowHouse.raw,
    });
    setEditProjectInput(rowHouse.project || '');
    setEditTypeInput(rowHouse.type || '');
    setEditFacingInput(rowHouse.facing || '');
    setEditDialogOpen(true);
  };

  const handleOpenAdd = () => {
    let projectId = '';
    if (pendingProjectFilter) {
      projectId = pendingProjectFilter;
    }
    history.push(`/app/project/rowhouse/add${projectId ? `?projectId=${projectId}` : ''}`);
  };

  const handleCloseEdit = () => {
    setEditFormData(null);
    setEditDialogOpen(false);
    setEditProjectInput('');
    setEditTypeInput('');
    setEditFacingInput('');
  };


  const calculateAmount = (formData, size, rate, plcPercent, discountPercent) => {
    const parseNumeric = (val) => {
      if (typeof val === 'number') return val;
      if (!val || val === '—' || val === '') return 0;
      const numStr = val.toString()
        .replace(/[₹,\s]/g, '')
        .replace(/ft|sq\.ft|\/|sq\.|%/g, '')
        .trim();
      const parsed = parseFloat(numStr);
      return isNaN(parsed) ? 0 : parsed;
    };

    const parsePLC = (val) => {
      if (typeof val === 'number') return val;
      if (!val || val === '—' || val === '') return 0;
      const numStr = val.toString().replace(/[%,\s]/g, '').trim();
      const parsed = parseFloat(numStr);
      return isNaN(parsed) ? 0 : parsed;
    };

    const discountNum = (() => {
      if (typeof discountPercent === 'number') return discountPercent;
      if (discountPercent === '' || discountPercent === null || discountPercent === undefined) {
        return formData.companyDiscountPercent ? parseFloat(formData.companyDiscountPercent) || 0 : 0;
      }
      const parsed = parseFloat(discountPercent);
      return isNaN(parsed) ? 0 : parsed;
    })();

    const sizeNum = parseNumeric(size);
    const rateNum = parseNumeric(rate);
    const plcValue = parsePLC(plcPercent);

    if (sizeNum > 0 && rateNum > 0) {
      const discountPerSqFt = rateNum * (discountNum / 100);
      const discountAmount = sizeNum * discountPerSqFt;
      const discountedRate = rateNum - discountPerSqFt;
      const baseAmount = sizeNum * discountedRate;
      const finalAmount = plcValue > 0
        ? baseAmount + sizeNum * rateNum * (plcValue / 100)
        : baseAmount;

      return { 
        ...formData, 
        amount: finalAmount.toString(),
        discountPerSqFt: discountPerSqFt.toFixed(2),
        discountAmount: discountAmount.toFixed(2)
      };
    }

    return formData;
  };

  const handleEditFieldChange = (field, value) => {
    if (field === 'project') {
      setEditProjectInput(value);
    }
    if (field === 'type') {
      setEditTypeInput(value);
    }
    if (field === 'facing') {
      setEditFacingInput(value);
    }
    setEditFormData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      const sizeValue = computeSizeFromDimensions(next.width, next.depth);
      next.size = sizeValue;
      const rate = field === 'rate' ? value : next.rate || '';
      const discountPercent = field === 'companyDiscountPercent' ? value : next.companyDiscountPercent || '';
      const plcPercent = field === 'plcPercent' ? value : next.plcPercent || '';
      return calculateAmount(next, sizeValue, rate, plcPercent, discountPercent);
    });
  };



  const handleSaveEdit = async () => {
    if (!editFormData || !editFormData.raw) {
      showTransientError('Invalid row house data. Please try again.');
      return;
    }

    setSavingEdit(true);
    try {
      const plotId = editFormData.raw._id;
      if (!plotId) {
        throw new Error('Row house ID not found');
      }

      if (!editFormData.number?.trim()) {
        throw new Error('Row house number is required');
      }

      const parseNumber = (value) => {
        if (typeof value === 'number') return value;
        if (!value || value === '—' || value === '') return 0;
        const numStr = value.toString()
          .replace(/[₹,\s]/g, '')
          .replace(/ft|sq\.ft|\/|sq\.|%|/g, '')
          .trim();
        const parsed = parseFloat(numStr);
        return isNaN(parsed) ? 0 : parsed;
      };

      const statusValue = editFormData.status && editFormData.status.trim()
        ? editFormData.status.toLowerCase()
        : 'available';

      const updateData = {
        plotName: editFormData.number.trim(),
        width: parseNumber(editFormData.width),
        length: parseNumber(editFormData.depth),
        totalSize: parseNumber(editFormData.size),
        facing: editFormData.facing || '',
        pricePerSqFt: parseNumber(editFormData.rate),
        companyDiscountPercent: parseNumber(editFormData.companyDiscountPercent),
        discountPerSqFt: parseNumber(editFormData.discountPerSqFt),
        discountAmount: parseNumber(editFormData.discountAmount),
        plcPercent: parseNumber(editFormData.plcPercent),
        amount: parseNumber(editFormData.amount),
        guidelineValue: parseNumber(editFormData.guidelineValue),
        status: statusValue,
        remark: editFormData.remark || '',
      };

      try {
        await plotService.updatePlotInventory(plotId, updateData);
      } catch (error) {
        throw error;
      }

      setRowHouses((prev) =>
        prev.map((item) => (item.id === editFormData.id ? editFormData : item)),
      );

      showTransientSuccess('Row house updated successfully!');
      handleCloseEdit();

      await fetchRowHouseInventory(page, pendingProjectFilter);
    } catch (error) {
      showTransientError(error.message || 'Failed to update row house. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (rowHouse) => {
    if (!rowHouse) return;
    if (!window.confirm(`Delete ${rowHouse.number || 'this row house'}?`)) return;

    const plotId = rowHouse.raw?._id || rowHouse.id;
    if (!plotId) {
      showTransientError('Row house ID not found.');
      return;
    }

    setDeleteLoadingId(rowHouse.id);
    try {
      await plotService.deletePlotInventory(plotId);
      setRowHouses((prev) => prev.filter((item) => item.id !== rowHouse.id));
      setTotalRowHouses((prev) => Math.max(0, prev - 1));
      showTransientSuccess('Row house deleted successfully!');
      await fetchRowHouseInventory(page, pendingProjectFilter);
    } catch (error) {
      showTransientError(error.message || 'Failed to delete row house.');
    } finally {
      setDeleteLoadingId(null);
    }
  };



  const handleAddNew = () => {
    handleOpenAdd();
  };

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Row House Inventory. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{ 
              backgroundColor: '#536DFE', 
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none' 
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ pr: { xs: 2, md: 5 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 3, gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RowHouseIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4" fontWeight="bold" color="primary">
              Row House List
            </Typography>
          </Box>
          {projectFilter && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
              Project: {projectFilter}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            sx={{ px: 2.5, py: 1.3, textTransform: 'none', fontWeight: 600 }}
          >
            Reset
          </Button>
          {canWrite && (
            <>
              <Button
                variant="contained"
                startIcon={<FileUploadIcon />}
                onClick={handleImportClick}
                sx={{ px: 2.5, py: 1.3 }}
              >
                Import
              </Button>
              <ExportButton
                onExportPdf={handleExportPDF}
                onExportCsv={handleExportCSV}
                onExportExcel={handleExportExcel}
                variant="outlined"
                sx={{ px: 2.5, py: 1.3 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                disabled={!pendingProjectFilter}
                sx={{ px: 3, py: 1.5 }}
              >
                Add Row House
              </Button>
            </>
          )}
          <RefreshButton
            onClick={() => fetchRowHouseInventory(page, pendingProjectFilter)}
            loading={loadingRowHouses}
            disabled={!pendingProjectFilter}
            sx={{
              height: { xs: 'auto', sm: 40 },
              px: 2.5,
            }}
          />
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <InputLabel>Select Project</InputLabel>
              <Select
                value={pendingProjectFilter}
                onChange={(e) => {
                  const val = e.target.value || '';
                  setPendingProjectFilter(val);
                  const proj = projectOptions.find((p) => String(p._id || p.id) === String(val));
                  setProjectFilter(proj ? (proj.projectName || proj.name || '') : '');
                  setPage(0);
                  if (!val) {
                    setRowHouses([]);
                    setTotalRowHouses(0);
                  }
                }}
                label="Select Project"
                disabled={loadingProjects || !projectOptions.length}
                sx={{ height: '56px' }}
              >
                <MenuItem value="">
                  <em>Select project</em>
                </MenuItem>
                {projectOptions.map((proj) => {
                  const id = proj._id || proj.id;
                  const name = proj.projectName || proj.name || '';
                  return (
                    <MenuItem key={id} value={String(id)}>
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {!loadingRowHouses && (
        <>
          {pendingProjectFilter && filteredRowHouses.length > 0 ? (
            <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Row House No.</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Width/Depth</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Area</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Facing</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">
                        Discount / Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">
                        Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Remark</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRowHouses.map((rowHouse, index) => {
                      const isEditable = rowHouse.isEdit !== false;
                      return (
                        <TableRow
                          key={rowHouse.id}
                          hover={isEditable}
                          sx={{
                            ...(isEditable ? {} : {
                              opacity: 0.5,
                              filter: 'blur(1px)',
                              pointerEvents: 'none',
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                              },
                              '& td': {
                                color: 'text.disabled',
                              },
                            }),
                          }}
                        >
                          <TableCell>
                            <Typography fontWeight="600">
                              {rowHouse.number || page * PAGE_SIZE + index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{rowHouse.width} x {rowHouse.depth}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{rowHouse.size} sq.ft</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={rowHouse.type} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{rowHouse.facing}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                              <Typography variant="body2" color="text.secondary">
                                {rowHouse.formattedDiscountPerSqFt}/sq.ft
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {rowHouse.formattedDiscountAmount}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="600">{rowHouse.amount}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={rowHouse.status} size="small" sx={statusChipStyle(rowHouse.status)} />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            >
                              {rowHouse.remark}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title={isEditable ? "View" : "Not editable"}>
                                <span>
                                  <IconButton
                                    color="info"
                                    size="small"
                                    onClick={() => handleOpenView(rowHouse)}
                                    disabled={!isEditable}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {canWrite ? (
                                <>
                                  <Tooltip title={isEditable ? "Edit" : "Not editable"}>
                                    <span>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => handleOpenEdit(rowHouse)}
                                        disabled={!isEditable}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={isEditable ? "Delete" : "Not editable"}>
                                    <span>
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() => handleDelete(rowHouse)}
                                        disabled={!isEditable || deleteLoadingId === rowHouse.id}
                                      >
                                        {deleteLoadingId === rowHouse.id ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <DeleteIcon />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              ) : (
                              <></>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalRowHouses}
                rowsPerPage={PAGE_SIZE}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={() => setPage(0)}
              />
            </Paper>
          ) : (
            <Card
              sx={{
                p: 10,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                backgroundColor: 'white',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {pendingProjectFilter ? 'No Row Houses Found' : 'No Project Selected'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {pendingProjectFilter
                  ? 'There are no row houses currently listed for this project.'
                  : 'Please select a row house project from the dropdown to view its inventory.'}
              </Typography>
              {!pendingProjectFilter && (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
                  Select a project to explore row house details, pricing, and availability.
                </Typography>
              )}
            </Card>
          )}
        </>
      )}



      <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight="bold">
            Row House Details - {selectedRowHouse?.number}
          </Typography>
          <IconButton onClick={handleCloseView} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedRowHouse && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Project"
                  value={selectedRowHouse.project || '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Row House Number"
                  value={selectedRowHouse.number || '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Width (ft)"
                  value={selectedRowHouse.raw?.width != null ? selectedRowHouse.raw.width : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Depth (ft)"
                  value={selectedRowHouse.raw?.length != null ? selectedRowHouse.raw.length : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Size (sq.ft)"
                  value={selectedRowHouse.raw?.totalSize != null ? selectedRowHouse.raw.totalSize : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Type"
                  value={selectedRowHouse.type || '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Facing"
                  value={selectedRowHouse.facing || '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Rate (₹/sq.ft)"
                  value={selectedRowHouse.raw?.pricePerSqFt != null ? selectedRowHouse.raw.pricePerSqFt : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Discount (%)"
                  value={
                    selectedRowHouse.raw?.companyDiscountPercent != null
                      ? selectedRowHouse.raw.companyDiscountPercent
                      : '—'
                  }
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="PLC Percent (%)"
                  value={selectedRowHouse.raw?.plcPercent != null ? selectedRowHouse.raw.plcPercent : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Discount Per Sq.ft (₹)"
                  value={selectedRowHouse.raw?.discountPerSqFt != null ? selectedRowHouse.raw.discountPerSqFt : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Discount Amount (₹)"
                  value={selectedRowHouse.raw?.discountAmount != null ? selectedRowHouse.raw.discountAmount : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Amount (₹)"
                  value={selectedRowHouse.raw?.amount != null ? selectedRowHouse.raw.amount : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Circle Rate
(₹)"
                  value={selectedRowHouse.raw?.guidelineValue != null ? selectedRowHouse.raw.guidelineValue : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Status"
                  value={selectedRowHouse.status || '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Remark"
                  value={selectedRowHouse.remark || '—'}
                  fullWidth
                  multiline
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseView} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight="bold">Edit Row House - {editFormData?.number}</Typography>
          <IconButton onClick={handleCloseEdit} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {editFormData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Project"
                  value={editFormData.project || ''}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Row House Number"
                  value={editFormData.number || ''}
                  onChange={(e) => handleEditFieldChange('number', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Width (ft)"
                  value={editFormData.width || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Only allow numbers up to 7 digits
                    if (val === '' || /^\d{0,7}$/.test(val)) {
                      handleEditFieldChange('width', val);
                    }
                  }}
                  fullWidth
                  inputProps={{ inputMode: 'numeric', maxLength: 7 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Depth (ft)"
                  value={editFormData.depth || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Only allow numbers up to 7 digits
                    if (val === '' || /^\d{0,7}$/.test(val)) {
                      handleEditFieldChange('depth', val);
                    }
                  }}
                  fullWidth
                  inputProps={{ inputMode: 'numeric', maxLength: 7 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Size (sq.ft)"
                  value={editFormData.size || ''}
                  fullWidth
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#666',
                      bgcolor: '#f5f5f5',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: '#666',
                    },
                  }}
                  helperText="Auto-calculated: Width × Depth"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  freeSolo
                  options={typeOptions}
                  value={editFormData.type || ''}
                  inputValue={editTypeInput}
                  onInputChange={(event, newInputValue) => {
                    setEditTypeInput(newInputValue);
                    handleEditFieldChange('type', newInputValue || '');
                  }}
                  onChange={(event, newValue) => handleEditFieldChange('type', newValue || '')}
                  renderInput={(params) => <TextField {...params} label="Please Select Type" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  freeSolo
                  options={facingOptions}
                  value={editFormData.facing || ''}
                  inputValue={editFacingInput}
                  onInputChange={(event, newInputValue) => {
                    setEditFacingInput(newInputValue);
                    handleEditFieldChange('facing', newInputValue || '');
                  }}
                  onChange={(event, newValue) => handleEditFieldChange('facing', newValue || '')}
                  renderInput={(params) => <TextField {...params} label="Facing" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Rate (₹/sq.ft)"
                  type="number"
                  value={editFormData.rate || ''}
                  onChange={(e) => handleEditFieldChange('rate', e.target.value)}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Discount (%)"
                  value={editFormData.companyDiscountPercent || ''}
                  onChange={(e) => handleEditFieldChange('companyDiscountPercent', e.target.value)}
                  fullWidth
                  inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="PLC Percent (%)"
                  type="number"
                  value={editFormData.plcPercent || ''}
                  onChange={(e) => handleEditFieldChange('plcPercent', e.target.value)}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Discount Per Sq.ft (₹)"
                  value={editFormData.discountPerSqFt || ''}
                  fullWidth
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#666',
                      bgcolor: '#f5f5f5',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: '#666',
                    },
                  }}
                  helperText="Auto-calculated"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Discount Amount (₹)"
                  value={editFormData.discountAmount || ''}
                  fullWidth
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#666',
                      bgcolor: '#f5f5f5',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: '#666',
                    },
                  }}
                  helperText="Auto-calculated"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Amount (₹)"
                  value={editFormData.amount || ''}
                  fullWidth
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#666',
                      bgcolor: '#f5f5f5',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: '#666',
                    },
                  }}
                  helperText="Auto-calculated (Discounted)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Circle Rate (₹)"
                  type="number"
                  value={editFormData.guidelineValue || ''}
                  onChange={(e) => handleEditFieldChange('guidelineValue', e.target.value)}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={editFormData.status || ''}
                    onChange={(e) => handleEditFieldChange('status', e.target.value)}
                    disabled={loadingProgress}
                  >
                    <MenuItem value="">
                      {loadingProgress ? 'Loading...' : 'Select status'}
                    </MenuItem>
                    {progressOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Remark"
                  value={editFormData.remark || ''}
                  onChange={(e) => handleEditFieldChange('remark', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEdit} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={savingEdit}
            startIcon={savingEdit ? <CircularProgress size={20} /> : null}
          >
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Row Houses</DialogTitle>
        <DialogContent dividers>
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError('')}>
              {importError}
            </Alert>
          )}
          {importSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportSuccess('')}>
              {importSuccess}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Upload an Excel file (.xlsx or .xls) with columns such as <code>project</code>, <code>number</code>, <code>width</code>,
              <code>depth</code>, <code>size</code>, <code>type</code>, <code>facing</code>, <code>rate</code>, <code>plcPercent</code>,
              <code>amount</code>, <code>guidelineValue</code>, <code>status</code>, and <code>remark</code>.
            </Typography>
            <Button
              variant="text"
              startIcon={<FileDownloadIcon />}
              onClick={() => {
                try {
                  downloadExcelFile({
                    rows: [
                      {
                        project: 'Emerald Heights',
                        number: 'Row-1',
                        width: '25 ft',
                        depth: '40 ft',
                        size: '1000 sq.ft',
                        type: '3 BHK',
                        facing: 'East',
                        rate: '₹9,500 / sq.ft',
                        plcPercent: '4%',
                        amount: '₹95,00,000',
                        guidelineValue: '₹80,00,000',
                        status: '',
                        remark: 'Ready for possession by Sep 2025',
                      },
                    ],
                    sheetName: 'Sample',
                    fileName: 'rowhouse_import_sample.xlsx',
                  });
                } catch (err) {
                  setImportError(err.message || 'Failed to download sample file.');
                }
              }}
            >
              Download Sample Format
            </Button>
            <Button
              variant="text"
              startIcon={<FileDownloadIcon />}
              onClick={() => {
                try {
                  downloadExcelFile({
                    rows: [
                      {
                        project: '',
                        number: '',
                        width: '',
                        depth: '',
                        size: '',
                        type: '',
                        facing: '',
                        rate: '',
                        plcPercent: '',
                        amount: '',
                        guidelineValue: '',
                        status: '',
                        remark: '',
                      },
                    ],
                    sheetName: 'Template',
                    fileName: 'rowhouse_import_template.xlsx',
                  });
                } catch (err) {
                  setImportError(err.message || 'Failed to download template file.');
                }
              }}
            >
              Download Simple File
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                Choose File
              </Button>
              {importFileName && (
                <Typography variant="caption" color="text.secondary">
                  {importFileName}
                </Typography>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default RowHouse;
